<template>
  <div class="general-information">
    <div class="cards-wrapper">
      <div class="cards-column" v-for="cardsColumn in projectCardsData" :key="cardsColumn.key">
        <info-card
          v-for="card in cardsColumn.items"
          :key="card.title"
          :title="card.title || ''"
          :value="card.value"
          :bold="card.type === 'budget'"
          :clickable="card.type === 'serviceProviderWorker'"
          @clicked="showWorkerDetailsPopup"
        />
      </div>
    </div>
    <div v-if="detailedProject.comment" class="comment">
      <p class="header">{{ $t('project.general.info.additional.comment') }}</p>
      <p class="content">
        {{ detailedProject.comment }}
      </p>
    </div>
    <worker-details-popup
      v-if="detailedWorker"
      :visible-popup="displayWorkerDetailsPopup"
      :worker="detailedWorker"
      @closed="hideWorkerDetailsPopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { userRoleGroups } from '@/constants/auth';
import InfoCard from '@/components/ProjectDetails/InfoCard.vue';
import WorkerDetailsPopup from '@/components/popup/WorkerDetailsPopup.vue';

import { calculateDuration, formatStartDateWithTimeAndYear } from '@/utils/dateUtils';
import { loadWorkerDetails } from '@/services/repository';

export default {
  name: 'GeneralInformation',
  components: { InfoCard, WorkerDetailsPopup },
  data: () => ({
    displayWorkerDetailsPopup: false,
    detailedWorker: null,
  }),
  methods: {
    async showWorkerDetailsPopup() {
      if (this.detailedProject.serviceProviderWorker) {
        this.detailedWorker = await loadWorkerDetails(this.detailedProject.serviceProviderWorker.id);
        this.displayWorkerDetailsPopup = true;
      }
    },
    hideWorkerDetailsPopup() {
      this.detailedWorker = null;
      this.displayWorkerDetailsPopup = false;
    },
  },
  computed: {
    ...mapGetters(['profile', 'detailedProject', 'userRoleGroup']),
    isUserSP() {
      return this.userRoleGroup === userRoleGroups.SP;
    },
    ableToShowServiceWorker() {
      return (
        this.detailedProject.serviceProviderWorker && this.detailedProject.serviceProviderWorker.id !== this.profile.id
      );
    },
    projectCardsData() {
      const {
        testCard,
        endDate,
        author,
        serviceProviderWorker,
        manager,
        scanData,
      } = this.detailedProject;

      const {
        metaData,
      } = testCard;

      const {
        advancedOptions,
        proxySettings,
      } = metaData;

      const testingType = testCard.type;
      const start = scanData[scanData.length - 1]?.createdDate || null;
      const duration = calculateDuration(start, endDate);

      const authType = metaData.authentication?.useAuthentication ? metaData.authentication.authenticationObject.settingsMetaData.type : null;

      const authName = metaData.authentication?.useAuthentication ? metaData.authentication.authenticationObject.settingsMetaData.name : null;

      return [
        {
          key: 1,
          items: [
            {
              type: 'testingType',
              title: this.$t('project.general.info.testingType'),
              value: testingType && this.$t(`test.testType.${testingType}`),
            },
            {
              type: 'authorId',
              title: this.$t('project.general.info.creator'),
              value: `${author.firstName.replace(/^(.)/, match => match.toUpperCase())} ${author.lastName.replace(/^(.)/, match => match.toUpperCase())}`,
            },
            this.ableToShowServiceWorker
              ? {
                type: 'serviceProviderWorker',
                title: this.$t('project.general.info.serviceProviderWorker'),
                value: `${serviceProviderWorker.firstName} ${serviceProviderWorker.lastName}`,
              }
              : !this.isUserSP && {
                type: 'managerEmail',
                title: this.$t('project.general.info.managerEmail'),
                value: manager?.email,
              },
          ],
        },
        {
          key: 2,
          items: [
            {
              type: 'startDate',
              title: this.$t('project.general.info.start'),
              value: start ? formatStartDateWithTimeAndYear(start) : '?',
            },
            {
              type: 'endDate',
              title: this.$t('project.general.info.end'),
              value: endDate ? formatStartDateWithTimeAndYear(endDate) : '?',
            },
            {
              type: 'duration',
              title: this.$t('project.general.info.duration'),
              value: duration || '?',
            },
          ],
        },
        {
          key: 3,
          items: authType ? [
            {
              type: 'authType',
              title: this.$t('project.general.info.provider'),
              value: this.$t(`auth.type.${authType}`) || '?',
            },
            {
              type: 'authName',
              title: this.$t('project.general.info.key.name'),
              value: authName || '?',
            },
            {
              type: 'emptyValue',
              title: '',
              value: '',
            },
          ] : [],
        },
        {
          key: 4,
          items:
            this.ableToShowServiceWorker && !this.isUserSP
              ? [
                {
                  type: 'managerEmail',
                  title: this.$t('project.general.info.managerEmail'),
                  value: manager?.email,
                },
              ]
              : [],
        },
        {
          key: 5,
          items:
            advancedOptions?.specifyPath
              ? [
                {
                  type: 'protocol',
                  title: this.$t('project.general.info.advancedOptions.protocol'),
                  value: advancedOptions.protocol,
                },
                {
                  type: 'path',
                  title: this.$t('project.general.info.advancedOptions.path'),
                  value: advancedOptions.path,
                },
                {
                  type: 'port',
                  title: this.$t('project.general.info.advancedOptions.port'),
                  value: advancedOptions.port,
                },
              ]
              : [],
        },
        {
          key: 6,
          items:
            proxySettings?.useProxy
              ? [
                {
                  type: 'ip',
                  title: this.$t('project.general.info.proxySettings.ip'),
                  value: proxySettings.address.ip,
                },
                {
                  type: 'port',
                  title: this.$t('project.general.info.proxySettings.port'),
                  value: proxySettings.address.port,
                },
                authName ? {
                  type: 'authName',
                  title: this.$t('project.general.info.key.name'),
                  value: authName || '?',
                } : {},
              ]
              : [],
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.cards-column {
  display: flex;
  flex-direction: column;
}

.cards {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex-grow: 1;
}

.cards-wrapper {
  display: flex;
  flex-direction: row;
}

.comment {
  margin-top: $space-15;

  .header {
    color: var(--text-tertiary);
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-2-5;
  }

  .content {
    color: var(--text-primary);
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-lg;
  }
}
</style>
